import { useEffect, useState } from "react";
import { useWorkspace } from "../../app/workspace";
import { Banner } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { ConfirmDialog, Dialog } from "../../components/ui/Dialog";
import { ErrorState } from "../../components/ui/ErrorState";
import { Field, Input } from "../../components/ui/Field";
import { Empty, Loading, Meter, Note, PageHeader } from "../../components/ui/Page";
import { Pill } from "../../components/ui/Pill";
import { SrOnly, Table, Td, Th } from "../../components/ui/Table";
import { useAction } from "../../hooks/useAction";
import { usePoll } from "../../hooks/usePoll";
import { useResource } from "../../hooks/useResource";
import type { StorageUsage, Video } from "../../lib/api/types";
import { videosApi } from "../../lib/api/videos";
import { formatBytes, formatDate, formatDuration } from "../../lib/format";
import { isSettling, isWorking, pendingQualities, processingPercent, VIDEO_TONE } from "../../lib/video-status";
import { DropZone } from "./DropZone";
import { UploadQueue } from "./UploadQueue";
import { useUploads } from "./uploads";

function StorageMeter({ usage }: { usage: StorageUsage }) {
  const { storageBytes: used, storageLimitBytes: limit } = usage;
  return (
    <div className="flex flex-wrap items-center gap-3 text-muted">
      <span className="font-mono text-xs uppercase tracking-widest">storage</span>
      <Meter value={used} max={limit} label="storage used" className="w-48" />
      <span className="font-mono text-xs tabular-nums">
        {formatBytes(used)} of {formatBytes(limit)} · {usage.files}{usage.fileLimit === null ? "" : ` of ${usage.fileLimit}`} files
      </span>
    </div>
  );
}

function RenameDialog({ video, onDone }: { video: Video | null; onDone: (v?: Video) => void }) {
  const [name, setName] = useState("");
  useEffect(() => { if (video) setName(video.name); }, [video]);
  const save = useAction(async () => onDone(await videosApi.rename(video!.id, name.trim())));
  return (
    <Dialog
      open={!!video}
      onClose={() => onDone()}
      title="Rename video"
      actions={<>
        <Button onClick={() => onDone()}>cancel</Button>
        <Button variant="primary" pending={save.pending} disabled={!name.trim()} onClick={() => void save.run()}>save</Button>
      </>}
    >
      <Field label="name">{({ id }) => <Input id={id} value={name} maxLength={200} onChange={(e) => setName(e.target.value)} autoFocus />}</Field>
      {save.error && <Banner tone="bad">{save.error}</Banner>}
    </Dialog>
  );
}

/** Keeps a long encode from looking stuck: a bar and a number that move, or "queued" until a server starts on it. */
function ProcessingProgress({ v }: { v: Video }) {
  if (!isSettling(v.status)) return null;
  const pct = processingPercent(v);
  return (
    <div className="flex items-center gap-2">
      {pct !== null && (
        <progress max={100} value={pct} aria-label={`${v.name} processing progress`}
          className="h-3 w-28 appearance-none overflow-hidden rounded-full border-2 border-ink [&::-moz-progress-bar]:rounded-full [&::-moz-progress-bar]:bg-ink [&::-webkit-progress-bar]:bg-paper [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-ink [&::-webkit-progress-value]:transition-[width] [&::-webkit-progress-value]:duration-500" />
      )}
      <span className="text-xs text-muted tabular-nums">{pct === null ? "queued" : `${pct}%`}</span>
    </div>
  );
}

function VideoRow({ v, canWrite, onRename, onDelete }: { v: Video; canWrite: boolean; onRename: () => void; onDelete: () => void }) {
  const download = useAction(async () => { location.assign(await videosApi.downloadUrl(v.id)); });
  return (
    <tr>
      <Td className="max-w-xs">
        <span className="block truncate font-bold" title={v.name}>{v.name}</span>
        <span className="block truncate text-xs text-muted" title={v.originalFilename}>{v.originalFilename}</span>
      </Td>
      <Td>
        <div className="flex flex-col items-start gap-1">
          <Pill tone={VIDEO_TONE[v.status]} pulse={isSettling(v.status)}>{v.status}</Pill>
          <ProcessingProgress v={v} />
          {pendingQualities(v).map((q) => <span key={q} className="text-xs text-muted tabular-nums">{q}</span>)}
          {v.failureReason && <span className="max-w-xs text-xs text-muted">{v.failureReason}</span>}
          {download.error && <span role="alert" className="text-xs font-bold text-link-hover">{download.error}</span>}
        </div>
      </Td>
      <Td className="text-muted">{formatDuration(v.durationMs)}</Td>
      <Td className="text-muted">{v.width && v.height ? `${v.width}×${v.height}` : "-"}{v.orientation ? ` · ${v.orientation}` : ""}</Td>
      <Td className="text-muted tabular-nums">{formatBytes(v.sizeBytes)}</Td>
      <Td className="text-muted">{formatDate(v.uploadedAt ?? v.createdAt)}</Td>
      <Td>
        <div className="flex justify-end gap-2">
          {v.status !== "uploading" && <Button size="sm" variant="ghost" pending={download.pending} onClick={() => void download.run()}>download</Button>}
          {canWrite && <Button size="sm" variant="ghost" onClick={onRename}>rename</Button>}
          {canWrite && <Button size="sm" variant="ghost" onClick={onDelete}>delete</Button>}
        </div>
      </Td>
    </tr>
  );
}

export function VideosPage() {
  const { workspace, can, writable } = useWorkspace();
  const uploads = useUploads();
  const lib = useResource((signal) => videosApi.list(workspace.id, signal), [workspace.id]);
  const [renaming, setRenaming] = useState<Video | null>(null);
  const [deleting, setDeleting] = useState<Video | null>(null);
  const remove = useAction(async (v: Video) => { await videosApi.remove(v.id); setDeleting(null); await lib.reload(); });

  // New uploads appear once they finish; processing videos update until they're ready.
  useEffect(() => { if (uploads.finishedCount) void lib.reload(); }, [uploads.finishedCount]);
  const settling = lib.data?.videos.some(isWorking) ?? false;
  usePoll(() => void lib.reload(), 5_000, settling);

  const canWrite = can("video.write");
  const videos = lib.data?.videos.filter((v) => v.status !== "uploading") ?? [];

  return (
    <>
      <PageHeader caption={`workspace: ${workspace.name.toLowerCase()}`} title="Videos" />
      {lib.data && <StorageMeter usage={lib.data.usage} />}
      {canWrite && writable && <DropZone onFiles={(files) => uploads.add(workspace.id, files)} />}
      <UploadQueue workspaceId={workspace.id} />

      {lib.error !== undefined && <ErrorState error={lib.error} onRetry={() => void lib.reload()} />}
      {!lib.data && lib.loading && <Loading />}
      {lib.data && videos.length === 0 && (
        <Empty title="No videos yet">Upload a video, and once it's processed you can add it to a stream's playlist.</Empty>
      )}
      {videos.length > 0 && (
        <Card raised>
          <Table label="Videos" minWidth={900}>
            <thead>
              <tr><Th>name</Th><Th>status</Th><Th>length</Th><Th>picture</Th><Th>size</Th><Th>added</Th><Th><SrOnly>actions</SrOnly></Th></tr>
            </thead>
            <tbody>
              {videos.map((v) => (
                <VideoRow key={v.id} v={v} canWrite={canWrite && writable} onRename={() => setRenaming(v)} onDelete={() => setDeleting(v)} />
              ))}
            </tbody>
          </Table>
        </Card>
      )}
      <Note>after upload, each video is processed into the qualities your plan streams in. long videos take a while; the percentage shows how far along it is.</Note>

      <RenameDialog video={renaming} onDone={(v) => { setRenaming(null); if (v) void lib.reload(); }} />
      <ConfirmDialog
        open={!!deleting}
        title={`Delete ${deleting?.name ?? "video"}?`}
        confirmLabel="delete video"
        danger
        pending={remove.pending}
        onConfirm={() => deleting && void remove.run(deleting)}
        onCancel={() => { setDeleting(null); remove.clearError(); }}
      >
        <p>The file and its processed copies are deleted. A stream that still plays it must drop it from its playlist first.</p>
        {remove.error && <Banner tone="bad">{remove.error}</Banner>}
      </ConfirmDialog>
    </>
  );
}
